import { useState, useCallback } from "react";
import { BlobServiceClient } from "@azure/storage-blob";
import { API_ENDPOINTS, AZURE_CONFIG, DEFAULT_CATEGORY } from "../constants/repositoryConstants";

/**
 * Hook for handling file uploads to Azure Blob Storage
 * @param {Object} options - Hook options
 * @param {Function} options.onSuccess - Callback when upload completes successfully
 * @param {Function} options.onError - Callback when upload fails
 * @param {string} options.reportName - Optional report name override
 * @returns {Object} { uploadFile, uploading, progress, error }
 */
const useFileUpload = ({ onSuccess, onError, reportName = null } = {}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const getEffectiveReportName = useCallback(() => {
    // If reportName is a non-empty string, use it
    if (typeof reportName === "string" && reportName.trim() !== "") {
      return reportName;
    }
    // If reportName is empty string, return null (should not upload without valid report name)
    if (reportName === "") {
      return null;
    }
    // If reportName is null/undefined, fall back to localStorage
    const storedReportName = localStorage.getItem("report_name");
    return storedReportName && storedReportName !== "undefined" ? storedReportName : "Repository";
  }, [reportName]);

  const uploadFile = useCallback(
    async (file, category = DEFAULT_CATEGORY) => {
      const companyid = localStorage.getItem("companyid");
      const username = localStorage.getItem("userName");
      const organization = localStorage.getItem("organization");
      const effectiveReportName = getEffectiveReportName();

      // Prevent upload if no valid report name
      if (!effectiveReportName) {
        const err = new Error("Report name is not available yet");
        setError(err.message);
        if (onError) {
          onError(err);
        }
        return { success: false, error: err };
      }

      setUploading(true);
      setProgress(0);
      setError(null);

      try {
        // Get SAS token
        const sasResponse = await fetch(
          `${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.GET_SAS_TOKEN}`,
          {
            method: "GET",
            headers: {
              "Content-type": "application/json; charset=UTF-8",
            },
          }
        );

        if (sasResponse.status !== 200) {
          throw new Error("Failed to fetch SAS token");
        }

        const sasJson = await sasResponse.json();
        const connectionString = sasJson.data;

        // Upload to Azure Blob Storage
        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient(AZURE_CONFIG.CONTAINER_NAME);
        const blobName = `${companyid}/repository/${effectiveReportName}/${file.name}`;
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();

        const tags = {
          category: category,
          username: organization,
          upload_datetime: new Date().toISOString(),
        };

        const uploadResponse = await blockBlobClient.uploadBrowserData(file, {
          blobHTTPHeaders: { blobContentType: file.type },
          blockSize: AZURE_CONFIG.BLOCK_SIZE,
          concurrency: AZURE_CONFIG.CONCURRENCY,
          onProgress: (ev) => {
            const percentComplete = Math.round((ev.loadedBytes / file.size) * 100);
            setProgress(percentComplete);
          },
        });

        if (uploadResponse) {
          localStorage.setItem("weblink", blobClient.url);
          await blockBlobClient.setMetadata(tags);

          // Notify backend
          const addFilesResponse = await fetch(
            `${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.ADD_FILES}`,
            {
              method: "POST",
              headers: {
                "Content-type": "application/json; charset=UTF-8",
                username: username,
              },
              body: JSON.stringify({
                company_id: companyid,
                report_name: effectiveReportName,
                file_name: file.name,
                environment: "Dev",
              }),
            }
          );

          if (addFilesResponse.status === 200) {
            const responseJson = await addFilesResponse.json();
            if (responseJson.success === false || responseJson.status === 400) {
              throw new Error(responseJson.data || responseJson.title || "Failed to register file");
            }

            setProgress(100);
            if (onSuccess) {
              onSuccess({ file, response: responseJson });
            }
            return { success: true, response: responseJson };
          } else {
            throw new Error("Failed to register file with backend");
          }
        }
      } catch (err) {
        console.error("Error uploading file:", err);
        setError(err.message || "Error uploading file");
        if (onError) {
          onError(err);
        }
        return { success: false, error: err };
      } finally {
        setUploading(false);
      }
    },
    [getEffectiveReportName, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setProgress(0);
    setError(null);
  }, []);

  return {
    uploadFile,
    uploading,
    progress,
    error,
    reset,
  };
};

export default useFileUpload;
